import { cloneDeep } from 'lodash';
import moment from 'moment';
import {
  keyValueFieldConfigs,
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SUPER_ADMIN
} from './constants';
import { isValidDate, isISODateString } from './global';
import { getProfileFromLocalStorage, getSalesRepOfSalesManager } from './session';
import { isAdmin, userHasAnyRole } from './roleHelpers';
import { setAttribute } from '../form-generator/helpers/utility';

const isDate = date => {
  return isValidDate(date) || isISODateString(date);
};

// Converts populated object to _id, to select appropriate option in FormGenerator
export const getObjectForPrefill = (initialObject = {}, formatDateForInput = false) => {
  const getValue = value => {
    return formatDateForInput && isDate(value) ? moment(value).format('YYYY-MM-DD') : value;
  };

  const newObject = {};
  Object.keys(initialObject).forEach(k => {
    if (initialObject[k] === undefined || initialObject[k] === null) return;

    if (k === 'additionalFields' && initialObject[k]) {
      Object.keys(initialObject[k]).forEach(ak => {
        const value = getValue(initialObject[k][ak]);
        newObject[`${ak}-additionalDochubField`] = value;
      });
      return;
    }

    // Hoisted DocHub lookup blob; form uses customEntities / *-customEntityField instead
    if (k === 'custom') {
      return;
    }

    if (k === 'customEntities' && initialObject[k]) {
      Object.keys(initialObject[k]).forEach(entityId => {
        const value = initialObject[k][entityId];
        if (value === undefined || value === null || value === '') return;
        newObject[`${entityId}-customEntityField`] = typeof value === 'object' && value._id ? value._id : value;
      });
      return;
    }

    if (typeof initialObject[k] === 'object') {
      if (
        Array.isArray(initialObject[k]) &&
        initialObject[k].length > 0 &&
        typeof initialObject[k][0] === 'object' &&
        initialObject[k][0].hasOwnProperty('_id')
      ) {
        newObject[k] = initialObject[k].map(obj => obj._id);
      } else if (initialObject[k].hasOwnProperty('_id')) {
        newObject[k] = initialObject[k]['_id'];
      } else {
        const value = getValue(initialObject[k]);
        newObject[k] = value;
      }
    } else {
      const value = getValue(initialObject[k]);
      newObject[k] = value;
    }
  });

  return newObject;
};

export const convertFormToKeyValueObject = submittedForm => {
  keyValueFieldConfigs.forEach(
    ({ name, isTableInput, isMultiText, defaultValue, shouldBeChecked, areKeysFixedOnDocument }) => {
      if (!submittedForm[name]) return;

      if (areKeysFixedOnDocument) {
        submittedForm[name] = submittedForm[name].map(keyValueField =>
          keyValueField.checked ? keyValueField.value || 'Inc' : ''
        );
        return;
      }

      if (isMultiText) {
        //for multi text inputs, must be converted to array of strings
        //for keyvalue and table inputs
        submittedForm[name] = submittedForm[name].map(value => value[0]).filter(Boolean);
      } else {
        //for keyvalue and table inputs
        submittedForm[name] = submittedForm[name]
          .filter(value => isTableInput || !shouldBeChecked || value.checked)
          .map(value => ({
            key: value[isTableInput && Array.isArray(value) ? 0 : 'key'],
            value: value[isTableInput && Array.isArray(value) ? 1 : 'value'] || defaultValue
          }));
      }
    }
  );
};

export const convertKeyValueToFormObject = (object, appChoices = []) => {
  keyValueFieldConfigs.forEach(({ name, label, isTableInput, isMultiText, defaultValue, areKeysFixedOnDocument }) => {
    if (!object[name]) return;

    //for fixed keys, it is stored as array, so converting to object form
    if (areKeysFixedOnDocument) {
      try {
        const keys = appChoices.find(a => a.key === label).values.filter((_, index) => object[name][index]);
        object[name] = object[name]
          .filter(value => value)
          .map((value, index) => ({ key: keys[index], value: value === defaultValue ? '' : value }));
      } catch (e) {}
      return;
    }

    //ONLY FOR KEY VALUE FIELDS
    object[name] = object[name].map(value =>
      isTableInput
        ? [value.key, value.value === defaultValue ? '' : value.value]
        : isMultiText
        ? [value]
        : {
            key: value.key,
            value: value.value === defaultValue ? '' : value.value
          }
    );
  });
};

export const addDocHubFormFieldsToForm = ({ formJson, appChoices = [], model = 'Contract' }) => {
  const newFormJson = cloneDeep(formJson);

  const additionalFields = getAdditionalFieldsColumns({ appChoices, model });

  if (additionalFields.length > 0) {
    newFormJson.forms[0].rows.push({
      collapsible: true,
      defaultCollapsed: true,
      rowName: 'Additional Doc Hub Fields',
      columns: [...additionalFields]
    });
  }

  return addCustomEntityFieldsToForm({ formJson: newFormJson, appChoices, model });
};

export const getEntityRecordLabel = (record = {}, columns = []) => {
  for (const column of columns) {
    const value = record?.[column.key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value);
    }
  }
  return record?._id ? String(record._id) : '';
};

export const getCustomEntityFieldsColumns = ({ appChoices, model = 'Contract', namesToUse }) => {
  const entityDefinitions = (appChoices.find(a => a.key === 'entityDefinitions')?.values || []).filter(
    definition =>
      fieldAppliesToModel(definition, model) &&
      (definition.columns || []).length > 0 &&
      (!namesToUse || namesToUse.includes(definition.name))
  );

  return entityDefinitions.map(definition => {
    const records = definition.records || [];
    const options = records.map(record => getEntityRecordLabel(record, definition.columns));
    const optionValues = records.map(record => record._id);
    const keyToUse = `${definition._id}-customEntityField`;

    return {
      default: 6,
      xs: 12,
      field: {
        id: keyToUse,
        key: keyToUse,
        title: definition.name,
        type: 'dropdown',
        options,
        optionValues,
        label: definition.name,
        showLabel: true
      }
    };
  });
};

export const addCustomEntityFieldsToForm = ({ formJson, appChoices = [], model = 'Contract' }) => {
  const newFormJson = cloneDeep(formJson);
  const customEntityFields = getCustomEntityFieldsColumns({ appChoices, model });

  if (customEntityFields.length > 0) {
    newFormJson.forms[0].rows.push({
      collapsible: true,
      defaultCollapsed: true,
      rowName: 'Custom Fields',
      columns: [...customEntityFields]
    });
  }

  return newFormJson;
};

export const getModelLabel = model => {
  return [model?.modelName, model?.modelNumber].filter(Boolean).join(' - ') || '-';
};

// Build model options: use populated model.modelName from backend directly, add appChoices for dropdown
export const getModelOptions = (appChoices, manufacturerId, populatedModel) => {
  // Get all models for dropdown from appChoices
  const allModels = (appChoices.find(a => a.key === 'model')?.values || []).filter(Boolean);
  const filtered = manufacturerId
    ? allModels.filter(
        m => (typeof m.manufacturer === 'object' ? m.manufacturer?._id : m.manufacturer) === manufacturerId
      )
    : [];

  // If backend sent populated model, use its modelName directly.
  if (populatedModel?.modelName && filtered.length === 0) {
    return { options: [getModelLabel(populatedModel)], values: [populatedModel._id] };
  }
  // If not than use the appChoices
  return filtered.length
    ? { options: filtered.map(getModelLabel), values: filtered.map(m => m._id) }
    : { options: [], values: [] };
};

export const fieldAppliesToModel = (field, model) => {
  if (Array.isArray(field?.models)) {
    return field.models.includes(model);
  }
  // Legacy single-model docs before migration
  return field?.model === model;
};

/** Prefer dropdownOptions (string[]); fall back to legacy options [{label,value}]. */
export const getFormFieldManualOptions = field => {
  if (Array.isArray(field?.dropdownOptions) && field.dropdownOptions.length) {
    return field.dropdownOptions.map(o => String(o).trim()).filter(Boolean);
  }

  return (field?.options || [])
    .map(o => {
      if (typeof o === 'string') return o.trim();
      return String(o?.value ?? o?.label ?? '').trim();
    })
    .filter(Boolean);
};

export const getAdditionalFieldsColumns = ({ appChoices, model = 'Contract', keysToUse }) => {
  const additionalFields = (appChoices.find(a => a.key === 'formFields')?.values || []).filter(
    v => fieldAppliesToModel(v, model) && (!keysToUse || keysToUse.includes(v.key))
  );

  return additionalFields.map(field => {
    const { label, key, type } = field;
    const optionStrings = getFormFieldManualOptions(field);
    const keyToUse = `${key}-additionalDochubField`;
    const typeToUse = type === 'date' ? 'text' : type;
    const variantToUse = type === 'date' ? type : undefined;

    return {
      default: 6,
      xs: 12,
      field: {
        id: keyToUse,
        key: keyToUse,
        title: label,
        type: typeToUse,
        variant: variantToUse,
        options: optionStrings,
        optionValues: optionStrings,
        label,
        showLabel: true
      }
    };
  });
};

/**
 * Sets up contactOwner dropdown options in a form based on the current user's roles (session).
 * @param {Object} params
 * @param {Array} params.appChoices - Application choices array
 * @param {Object} params.formJson - Form JSON object to modify
 */
export const setupContactOwnerDropdown = ({ appChoices, formJson }) => {
  if (!userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SALES_MANAGER)) {
    return;
  }

  const members = (isAdmin()
    ? appChoices
        .find(c => c.key === 'members')
        .values.filter(m => {
          if (!m) return false;
          const lowered = [];
          if (Array.isArray(m.roles) && m.roles.length) {
            m.roles.forEach(r => lowered.push(String(r).toLowerCase()));
          }
          if (m.role) lowered.push(String(m.role).toLowerCase());
          if (!lowered.length) return false;
          const allowed = [
            ROLE_SUPER_ADMIN,
            ROLE_ADMIN,
            ROLE_SALES,
            ROLE_SALES_MANAGER,
            ROLE_SALES_AND_INVENTORY_MANAGER
          ].map(r => r.toLowerCase());
          return lowered.some(r => allowed.includes(r));
        })
    : [getProfileFromLocalStorage(), ...getSalesRepOfSalesManager()]
  ).sort((m1, m2) => m1.name.localeCompare(m2.name));

  setAttribute(
    formJson,
    'contactOwner',
    'options',
    members.map(s => s['name'])
  );
  setAttribute(
    formJson,
    'contactOwner',
    'optionValues',
    members.map(s => s['_id'])
  );
};
